import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  getAllOffers,
  createOffer,
  updateOffer,
  deleteOffer,
  getAllActiveOffers,
  getOneOffer,
} from "../../../services/offers";

function useOffers(offerId, onSuccess = null) {
  const queryClient = useQueryClient();

  // Get all offers query (Admin only)
  const {
    data: offers,
    isLoading: isGettingOffers,
    error: offersError,
  } = useQuery({
    queryKey: ["offers"],
    queryFn: () => getAllOffers(),
  });

  // Get all active offers query (For users)
  const {
    data: activeOffers,
    isLoading: isGettingActiveOffers,
    error: activeOffersError,
  } = useQuery({
    queryKey: ["activeOffers"],
    queryFn: () => getAllActiveOffers(),
  });

  // Get single offer query
  const {
    data: oneOffer,
    isLoading: isLoadingOneOffer,
    error: isErrorOneOffer,
  } = useQuery({
    queryKey: ["offer", offerId],
    queryFn: () => getOneOffer(offerId),
    enabled: !!offerId,
  });

  // Create offer mutation (Admin only)
  const { mutate: createOfferFn, isLoading: isCreatingOffer } = useMutation({
    mutationFn: (offer) => {
      // console.log("Offer from query: ", offer);
      return createOffer(offer);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["offers"]);
      toast.success("Offer created successfully");
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      // console.log(error);
      toast.error(error.response?.data?.error || "Error creating offer");
    },
  });

  // Update offer mutation (Admin only)
  const { mutate: updateOfferFn, isLoading: isUpdatingOffer } = useMutation({
    mutationFn: ({ offerId, offer }) => {
      // console.log(offerId);
      // console.log(offer);
      return updateOffer(offerId, offer);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["offers"]);
      toast.success("Offer updated successfully");
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Error updating offer");
    },
  });

  // Delete offer mutation (Admin only)
  const { mutate: deleteOfferFn, isLoading: isDeletingOffer } = useMutation({
    mutationFn: (offerId) => deleteOffer(offerId),
    onSuccess: () => {
      queryClient.invalidateQueries(["offers"]);
      toast.success("Offer deleted successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Error deleting offer");
    },
  });

  return {
    // Queries
    offers,
    isGettingOffers,
    offersError,
    activeOffers,
    isGettingActiveOffers,
    activeOffersError,
    oneOffer,
    isLoading: isLoadingOneOffer,
    isErrorOneOffer,
    // Mutations
    createOffer: createOfferFn,
    isCreatingOffer,
    updateOffer: updateOfferFn,
    isUpdatingOffer,
    deleteOffer: deleteOfferFn,
    isDeletingOffer,
  };
}

export default useOffers;
